import React, { useEffect, useMemo, useState } from 'react';
import useGroupByGroupId from '../../../hooks/site/useGroupByGroupId';
import { useAppSelector } from '../../../store';
import { useDispatch } from 'react-redux';
import { deleteFSCampaign } from '../../../store/apps/freeSpinSlice';
import { ENUM_CUS_EVENT } from '../../../enums/enums';
import {
  Box,
  Button,
  Stack,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { dispatchCusEvent } from '../../../utils';
import FSInfoString from './ui/FSInfoString';
import { API_GROUP } from '../../../api/user';
import ConfirmDialog from '../../../components/shared/ConfirmDialog';
import InfoDialog from '../../../components/shared/InfoDialog';
import useBootstrapSize from '../../../hooks/useBootstrapSize';
import { IconTrash } from '@tabler/icons-react';
import useColor from '../../../hooks/site/useColor';
import useGetBonuses from '../../../hooks/site/useGetBonuses';
import Table from '../../../components/material-ui/adaptive/Table';
import ShowContainer from '../../../components/material-ui/ShowContainer';
import ErrorBoundary from '../../../components/material-ui/error_boundary/ErrorBoundary';
import CusPageContainer from '../../../components/shared/CusPageContainer';
import Card from '../../../components/material-ui/adaptive/Card';
import BtnEdit from '../../../components/bonus/ui/BtnEdit';
import { styleCellTitle } from '../../../components/bonus/ui/styles';
import CusInput from '../../../components/material-ui/CusInput';
import ChangeFsName from './ui/ChangeFsName';
import FlexContainer from '../../../components/material-ui/adaptive/FlexContainer';
import Scrollbar from '../../../components/custom-scroll/Scrollbar';

export default function FSList() {
  const { group, groupId } = useGroupByGroupId();
  useGetBonuses();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { primary } = useColor();
  const { isMobile } = useBootstrapSize();
  const campaignByGroupId = useAppSelector((state) => state.freeSpin.campaignByGroupId);

  const [openDelete, setOpenDelete] = useState(false); // boolean || JSX
  const [openInfo, setOpenInfo] = useState(false); // JSX || boolean
  const [deleteId, setDeleteId] = useState(null);
  const [type, setType] = useState(false);

  const primaryColor = primary.main;

  const campaigns = campaignByGroupId?.[groupId];
  const sortCampaigns = useMemo(() => {
    if (!campaigns) return;
    if (!Array.isArray(campaigns)) return;
    return [...campaigns]?.sort((a, b) => a.campaignId - b.campaignId);
  });

  const fillBonusContent = (bonuses, type, freespinName, campaignId) => {
    const isDelete = type === 'delete';
    const isEdit = type === 'edit';

    if (!bonuses) return <></>;

    return (
      <ErrorBoundary>
        <Box>
          <ShowContainer condition={isDelete}>
            <Typography mt={2} variant="caption" fontSize={16}>
              {t('Deletion is not possible, the campaign is used in bonuses')}
            </Typography>
          </ShowContainer>

          <ShowContainer condition={isEdit}>
            <Box display={'flex'} gap={1} flexDirection={'column'}>
              <FlexContainer gap={1} alignItems={'auto'}>
                <Typography variant="caption" fontSize={16}>
                  {t('Bonuses that use campaign')}
                </Typography>
                <ChangeFsName
                  freespinName={freespinName}
                  campaignId={campaignId}
                  groupId={groupId}
                  setOpenInfo={setOpenInfo}
                />
              </FlexContainer>
              <Typography variant="caption" fontSize={16}>
                {t('You can edit the freespin template if it is not used in bonuses.')}
              </Typography>
            </Box>
          </ShowContainer>

          <Stack display={'flex'} flexDirection={'column'} gap={1} mt={2}>
            {bonuses.map((bonus, indx) => {
              const getName = () => {
                if (bonus.bonusId) return t('bonus') + ': ' + bonus.bonusId;
                return t('bonus') + ': ' + indx;
              };
              const type = bonus.type.toLowerCase();
              const name = getName();

              return (
                <Typography
                  variant="body1"
                  color="primary"
                  onClick={() => navigate(`/bonuses/${type}/${groupId}`)}
                  style={{ cursor: 'pointer' }}>
                  #{name}
                </Typography>
              );
            })}
          </Stack>
        </Box>
      </ErrorBoundary>
    );
  };

  const onAddCompany = () => navigate(`/freespin/${groupId}/new`);
  const onEditClick = (el) => {
    const id = el.campaignId;
    if (el.editable) {
      navigate(`/freespin/${groupId}/${id}`);
      return;
    }

    const currentFreespin = sortCampaigns.find((el) => el.campaignId === id);

    const JSX = fillBonusContent(el.bonuses, 'edit', currentFreespin.name, id);
    setType('edit');
    setOpenInfo(JSX);
  };
  const onDeleteClick = (el) => {
    const id = el.campaignId;
    if (el.bonuses) {
      const JSX = fillBonusContent(el.bonuses, 'delete');
      setType('delete');
      setOpenInfo(JSX);
    } else {
      setOpenDelete(<></>);
      setDeleteId(id);
    }
  };

  const onDelete = (id) => {
    API_GROUP.bonus.freespin.campaign
      .delete({ campaignId: id })
      .then(() => {
        dispatch(deleteFSCampaign({ groupId, campaignId: id }));
      })
      .catch(console.error);
  };

  useEffect(() => {
    dispatchCusEvent(ENUM_CUS_EVENT.getFreespinList, { groupId });
  }, [groupId]);

  const PlugNoCampaign = () => (
    <Stack mt={3} gap={3} display={'flex'} flexDirection={'column'} alignSelf={'center'}>
      <Typography variant="h3">
        {t("You don't have any freespins templates for this group yet.")}
      </Typography>

      <Box>
        <Button variant="text" onClick={onAddCompany}>
          {t('Add a template')}
        </Button>
      </Box>
    </Stack>
  );

  return (
    <>
      <CusPageContainer
        title="List of freespins templates"
        urlById={(id) => `/freespin/${id}/list`}
        group={group}
        loader={!group}>
        <Card>
          <ShowContainer condition={campaigns && campaigns.length} plug={<PlugNoCampaign />}>
            <Stack display={'flex'} flexDirection={'row'} justifyContent={'end'} gap={1}>
              <Button variant="text" onClick={onAddCompany}>
                {t('Create a new template')}
              </Button>
            </Stack>
            <Scrollbar sx={{ maxHeight: { lg: 'calc(100vh - 350px)', md: '100dvh' } }}>
              <TableContainer sx={{ minWidth: { sm: '380px' } }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ p: 1 }} style={styleCellTitle}>
                        {t('ID')}
                      </TableCell>
                      <TableCell style={styleCellTitle}>{t('Freespins template')}</TableCell>
                      <TableCell style={styleCellTitle}>{t('Currency')}</TableCell>
                      {!isMobile && <TableCell style={styleCellTitle}>{t('Action')}</TableCell>}
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {sortCampaigns?.map((el, indx) => (
                      <ErrorBoundary key={indx}>
                        <TableRow>
                          <TableCell sx={{ p: 1 }}>{el.campaignId}</TableCell>
                          <TableCell>
                            <FSInfoString company={el} isColumn />
                          </TableCell>
                          <TableCell>{el.currency}</TableCell>
                          <TableCell height={55}>
                            {isMobile ? (
                              <Box position={'relative'} padding={0} sx={{ left: -10 }}>
                                <BtnEdit
                                  onEditClick={() => onEditClick(el)}
                                  variant={2}
                                  style={{ position: 'absolute', bottom: 0, cursor: 'pointer' }}
                                />
                                <Box
                                  onClick={() => onDeleteClick(el)}
                                  style={{ position: 'absolute', top: 1, cursor: 'pointer' }}>
                                  <IconTrash color={primaryColor} height={20} />
                                </Box>
                              </Box>
                            ) : (
                              <Stack display={'flex'} flexDirection={'row'} gap={1}>
                                <ShowContainer condition={el.editable || el.bonuses}>
                                  <Button fullWidth onClick={() => onEditClick(el)}>
                                    {t('Edit')}
                                  </Button>
                                </ShowContainer>
                                <Button fullWidth onClick={() => onDeleteClick(el)}>
                                  {t('Delete')}
                                </Button>
                              </Stack>
                            )}
                          </TableCell>
                        </TableRow>
                      </ErrorBoundary>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Scrollbar>
          </ShowContainer>
        </Card>
      </CusPageContainer>

      <ConfirmDialog
        open={openDelete}
        title={t('Confirm deletion')}
        content={t('Are you sure you want to delete this item? This action cannot be undone.')}
        onClose={() => setOpenDelete(false)}
        onConfirm={() => onDelete(deleteId)}
        contentJSX={openDelete}
      />

      <InfoDialog
        open={!!openInfo}
        onClose={() => setOpenInfo(false)}
        title={type == 'edit' ? '' : 'Action is impossible'}
        infoJSX={openInfo}
      />
    </>
  );
}
